/**
 * @file AncienSportifCrossfit.tsx
 * @summary Article de blog — AncienSportifCrossfit
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import SEO from "@/components/SEO";
import BlogHeroImage, { getArticleImageUrls, withArticleImages, slugFromUrl } from "@/components/blog/BlogHeroImage";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowLeft, Trophy, Flame, RotateCw, Users } from "lucide-react";
import BlogCTA, { BlogStickyCTA } from "@/components/blog/BlogCTA";

const URL = "https://crossfitchistera.fr/blog/ancien-sportif-crossfit";

const SLUG = slugFromUrl(URL);
const IMAGES = getArticleImageUrls(SLUG, 5);
const faq = [
  { "@type": "Question", name: "J'ai arrêté le sport depuis 5 ans, c'est trop tard ?", acceptedAnswer: { "@type": "Answer", text: "Non. Le corps retrouve très vite ses sensations quand il a déjà été entraîné. Les coachs structurent la reprise pour éviter de vouloir aller trop vite." } },
  { "@type": "Question", name: "J'ai d'anciennes blessures (genou, dos, épaule), c'est compatible ?", acceptedAnswer: { "@type": "Answer", text: "Oui dans la plupart des cas, à condition d'avoir l'accord de votre médecin et de bien briefer le coach. Les mouvements peuvent être adaptés pour ne pas solliciter une zone fragile." } },
  { "@type": "Question", name: "Le CrossFit peut-il remplacer mon ancien sport ?", acceptedAnswer: { "@type": "Answer", text: "Pour beaucoup d'anciens sportifs, oui : variété, intensité, esprit collectif. Pour d'autres, c'est un excellent complément qui prépare physiquement à reprendre la course, le rugby, le vélo." } },
  { "@type": "Question", name: "Vais-je retrouver mon niveau d'avant ?", acceptedAnswer: { "@type": "Answer", text: "Souvent, oui, et parfois mieux. La progression dépend de la régularité et de la patience initiale. Le piège : vouloir reprendre comme si on n'avait jamais arrêté." } },
];

const jsonLd = [
  { "@context": "https://schema.org", "@type": "Article", headline: "Ancien sportif : pourquoi le CrossFit redonne envie", description: "Blessures, perte de motivation, routine… découvrez pourquoi d'anciens sportifs retrouvent le plaisir du sport chez Chistera.", author: { "@type": "Organization", name: "CrossFit Chistera" }, publisher: { "@type": "Organization", name: "CrossFit Chistera" }, mainEntityOfPage: URL, inLanguage: "fr-FR" },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
];

const raisons = [
  { icon: Flame, title: "L'intensité retrouvée", desc: "Des séances qui mettent vraiment le cardio et les muscles au travail." },
  { icon: Trophy, title: "Le sens du résultat", desc: "Chaque WOD est mesurable : temps, charges, répétitions. On voit qu'on progresse." },
  { icon: Users, title: "L'esprit d'équipe", desc: "Comme dans un sport collectif : on s'encourage, on souffre ensemble, on rit après." },
  { icon: RotateCw, title: "La variété permanente", desc: "Une nouvelle séance chaque jour. Plus jamais de routine qui démotive." },
];

export default function AncienSportifCrossfit() {
  return (
    <>
      <SEO
        title="Ancien sportif : pourquoi le CrossFit redonne envie"
        description="Blessures, perte de motivation, routine… découvrez pourquoi d'anciens sportifs retrouvent le plaisir du sport chez Chistera."
        canonical={URL}
        jsonLd={withArticleImages(jsonLd, IMAGES)}
          ogImages={IMAGES}
      />
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-32 md:pb-20">
          <article className="container mx-auto px-4 max-w-4xl">
            <BlogHeroImage slug={SLUG} />
            <nav aria-label="Fil d'Ariane" className="mb-6 text-sm text-muted-foreground flex items-center flex-wrap gap-1">
              <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-3 h-3" /> Accueil</Link>
              <ChevronRight className="w-3 h-3" />
              <span>Blog</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground">Ancien sportif</span>
            </nav>

            <header className="mb-10">
              <span className="inline-block text-xs font-semibold text-gold tracking-wider mb-3 font-body normal-case uppercase">Témoignage · Lecture ~ 6 min</span>
              <h1 className="text-4xl md:text-6xl text-foreground mb-6 font-heading">« Je pensais avoir définitivement perdu le goût du sport »</h1>
              <p className="text-lg text-foreground/85 leading-relaxed mb-8">
                Il y a un profil qu'on retrouve souvent dans une box de CrossFit : <strong>les anciens sportifs</strong>. Ceux qui ont joué au rugby, au foot, au handball, qui faisaient du judo ou du tennis dans leur jeunesse — et qui ont décroché. Boulot, blessures, enfants, lassitude. Pour beaucoup, le CrossFit chez Chistera est devenu le déclic.
              </p>
              <BlogCTA variant="hero" primaryLabel="Tester un cours gratuitement" />
            </header>

            <aside className="border-l-4 border-gold bg-charcoal/40 px-6 py-5 mb-12 rounded-r-md">
              <p className="text-sm uppercase tracking-wider text-gold font-heading mb-2">Témoignage</p>
              <p className="text-foreground/90 leading-relaxed italic">
                « Je faisais toujours la même chose. Un peu de cardio, un peu de machines… mais sans vraie motivation. » — <strong className="not-italic">Antoine, 38 ans, ancien rugbyman</strong>
              </p>
            </aside>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground">
              <h2>« Je tournais en rond à la salle »</h2>
              <p>
                Antoine, 38 ans, ancien rugbyman, a essayé pendant deux ans de retrouver une activité régulière en salle de fitness. Sans succès. Trop solitaire, trop répétitif, sans vrai cap. Quand il pousse la porte de Chistera, il ne sait pas trop à quoi s'attendre. Premier WOD, premier chrono, premier coach qui corrige sa posture en deadlift. Quelque chose se rallume.
              </p>

              <h2>Ce qui change vraiment, pour un ancien sportif</h2>
              <p>
                Le CrossFit retrouve ce qui manquait depuis l'arrêt du sport en club : <strong>l'intensité, la performance mesurable, le collectif</strong>. On est chronométré, on se compare à soi-même, on partage l'effort avec un groupe. Et chaque séance est différente — pas de routine qui finit par lasser.
              </p>
            </div>

            <section className="my-12">
              <div className="grid md:grid-cols-2 gap-4">
                {raisons.map((r) => {
                  const Icon = r.icon;
                  return (
                    <div key={r.title} className="border border-border bg-card rounded-lg p-5 hover:border-gold/50 transition-colors">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                        <h3 className="font-heading text-lg text-foreground">{r.title}</h3>
                      </div>
                      <p className="text-foreground/80 text-sm font-body normal-case">{r.desc}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            <BlogCTA single primaryLabel="Réserver mon essai gratuit" />

            <div className="my-16 border border-gold/30 bg-charcoal/40 rounded-lg px-6 py-8 text-center">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Reprenez là où vous vous êtes arrêté</h2>
              <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">2 séances d'essai gratuites. Briefez le coach sur votre passé sportif, il adapte la première séance.</p>
              <BlogCTA />
            </div>

            <div className="prose prose-invert max-w-none prose-headings:font-heading prose-headings:text-foreground prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:text-foreground/85 prose-p:leading-relaxed prose-a:text-gold hover:prose-a:underline prose-strong:text-foreground">
              <h2>Le piège classique de la reprise</h2>
              <p>
                L'ancien sportif a un avantage — il connaît l'effort — et un piège : vouloir reprendre comme s'il n'avait jamais arrêté. Les blessures n'arrivent pas parce que le sport est dangereux, mais parce que la <strong>reprise est trop rapide</strong>. Les coachs de Chistera connaissent ce profil et savent doser : technique d'abord, charges ensuite, intensité progressive.
              </p>

              <h2>Une vraie communauté, à Chistera</h2>
              <p>
                Beaucoup d'anciens sportifs nous rejoignent depuis Mions, Heyrieux, Saint-Bonnet-de-Mure, Toussieu ou Chaponnay. Ils retrouvent ce qu'ils n'avaient plus depuis le club : un groupe, un coach qui connaît leur prénom, des objectifs partagés et l'envie de revenir.
              </p>
            </div>

            <section className="my-16">
              <h2 className="text-3xl font-heading text-foreground mb-6">Questions fréquentes</h2>
              <div className="space-y-4">
                {faq.map((q) => (
                  <details key={q.name} className="group border border-border bg-card rounded-lg p-5 open:border-gold/50">
                    <summary className="cursor-pointer font-heading text-lg text-foreground flex justify-between items-center gap-4 list-none">
                      <span>{q.name}</span>
                      <ChevronRight className="w-5 h-5 text-gold shrink-0 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-foreground/85 leading-relaxed font-body normal-case">{q.acceptedAnswer.text}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="my-16">
              <div className="border border-gold/30 bg-gradient-to-br from-charcoal/60 to-charcoal/20 rounded-lg px-6 py-10 text-center">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground mb-3">Le déclic est peut-être ici</h2>
                <p className="text-foreground/80 mb-6 max-w-xl mx-auto font-body normal-case">Venez essayer une séance. Si ça résonne, vous le saurez en 50 minutes.</p>
                <BlogCTA />
              </div>
            </section>

            <section className="my-12 border-t border-border pt-8">
              <h2 className="text-xl font-heading text-foreground mb-4">À lire aussi</h2>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm font-body normal-case">
                <li><Link to="/reprendre-le-sport" className="text-gold hover:underline">→ Reprendre le sport</Link></li>
                <li><Link to="/cours/hybrid-training" className="text-gold hover:underline">→ Hybrid Training (HYROX)</Link></li>
                <li><Link to="/blog/que-recherchez-vous-dans-le-crossfit" className="text-gold hover:underline">→ Que recherchez-vous dans le CrossFit ?</Link></li>
                <li><Link to="/cours/wod" className="text-gold hover:underline">→ Le cours WOD</Link></li>
              </ul>
            </section>
          </article>
        </main>
        <Footer />
        <BlogStickyCTA label="Tester un cours gratuitement" />
      </div>
    </>
  );
}
